import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { assertNever } from 'src/replicant/utils';
import { ImageView } from '@play-co/timestep-core/ui';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import ShineFx from 'src/game/minigame/match2-timestep/components/ShineFx';
import SparkleFx from 'src/game/minigame/match2-timestep/team/components/SparkleFx';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = () => void;
export type RevivalPopupInitArgs =
  | { mode: 'intro' }
  | { mode: 'hungry'; species: PetSpeciesID }
  | { mode: 'revived'; species: PetSpeciesID; friendName?: string };

export default class PopupRevivalTall extends PopupBasic {
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: 600,
      height: 759,
      boxOpts: {
        image: 'assets/ui/visit/revive/popup_green.png',
      },
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.REVIVE,
      },
    });

    this.buttonClose.removeFromSuperview();

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: -1,
      labelOffsetX: -4,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 630,
      width: 200,
      height: 80,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close();
      },
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.REVIVE_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/visit/revive/popup_green.png',
      'assets/ui/visit/revive/revival_potion.png',
    ]);
  }

  public init(args: RevivalPopupInitArgs) {
    super.init({});

    switch (args.mode) {
      case 'intro':
        this.createIntroContent();
        break;
      case 'hungry':
        this.createHungryContent(args);
        break;
      case 'revived':
        this.createRevivedContent(args);
        break;
      default:
        assertNever(args);
    }
  }

  private createIntroContent() {
    this.title.setText(() => i18n('friendsVisit.revive.intro.title'));
    new ImageView({
      superview: this.box,
      image: 'assets/ui/visit/revive/revival_potion.png',
      centerOnOrigin: true,
      width: 271,
      height: 272,
      x: this.box.style.width / 2,
      y: 255,
    });

    this.message.updateOpts({
      localeText: () => i18n('friendsVisit.revive.intro.message'),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      font: 'Title',
      size: 27,
      wordWrap: true,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 495,
    });
  }

  private createHungryContent({ species }: { species: PetSpeciesID }) {
    this.title.setText(() => i18n('friendsVisit.revive.hungry.title'));

    const pet = new PetEntity('revivalPet', species);
    pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 425,
      scale: 0.7,
      anchorX: 0.5 * pet.style.width,
      anchorY: 0.5 * pet.style.height,
    });
    pet.statusClip.updateOpts({
      y: 50,
    });

    const petAnimations = petAnimationNames[species];
    pet.setAnimation(petAnimations.hungry.idle);
    pet.setStatusAnimation('hungry');

    this.message.updateOpts({
      localeText: () => i18n('friendsVisit.revive.hungry.message'),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      font: 'Title',
      size: 27,
      wordWrap: true,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 495,
    });
  }

  private createRevivedContent({
    species,
    friendName,
  }: {
    species: PetSpeciesID;
    friendName?: string;
  }) {
    this.title.setText(() => i18n('friendsVisit.revive.revived.title'));

    const shine = new ShineFx({
      superview: this.box,
    });
    shine.show();

    shine.updateOpts({
      x: this.box.style.width / 2,
      y: 320,
      centerOnOrigin: true,
    });

    const pet = new PetEntity('revivalPet', species);
    pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 410,
      anchorX: 0.5 * pet.style.width,
      anchorY: 0.5 * pet.style.height,
    });

    new SparkleFx({
      superview: this.box,
      width: 400,
      height: 400,
      x: shine.style.x,
      y: shine.style.y,
      amount: 5,
      scale: 1.5,
      shineDuration: 3,
    });

    const petAnimations = petAnimationNames[species];
    pet.setAnimation(petAnimations.play);

    const message = friendName
      ? 'friendsVisit.revive.revived.message'
      : 'friendsVisit.revive.revived.messageGeneric';

    this.message.updateOpts({
      localeText: () => i18n(message, { friendName }),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      font: 'Title',
      size: 27,
      wordWrap: true,
      color: '#ffffff',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 510,
    });
  }
}
